import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import { useLanguage } from '../../../contexts/LanguageContext';

const { TextArea } = Input;

const TextAreaEditor = ({ formData, onFormChange }) => {
  const { t } = useLanguage();
  const [localFormData, setLocalFormData] = useState({});

  // 「預設值」TextArea 採用非受控（defaultValue + key）方式。
  // 原因：使用受控 value 時，Ant Design TextArea 在受控值從 '' 初始化為實際內容時，
  // 會觸發帶有舊 DOM 空值的虛假 onChange，導致 GrapesJS 內容被清空。
  //
  // 但 Ant Design 的 ResizableTextArea 在掛載時做 autoSize 計算，仍會觸發一次虛假
  // onChange（帶的是 defaultValue 的 trim 版本）。因此仍需 hasUserTyped 防護。
  const hasUserTyped = useRef(false);

  // 當 formData 變化時，同步到內部狀態
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setLocalFormData(formData);
      // 新組件載入時重置標記，防止上一個組件的虛假事件影響
      hasUserTyped.current = false;
    } else if (formData && Object.keys(formData).length === 0) {
      setLocalFormData({});
      hasUserTyped.current = false;
    }
  }, [formData]);

  // 處理非內容欄位的變更（placeholder / name / rows / cols / required）
  const handleFormChange = (field, value) => {
    setLocalFormData(prev => ({ ...prev, [field]: value }));
    onFormChange(field, value);
  };

  // key 以組件唯一標識為準：切換不同組件時強制重新掛載 TextArea（同時重置 defaultValue）
  // 同一組件反覆刷新時 key 不變，TextArea 保留用戶輸入
  const componentKey = (formData && (formData.name || formData.id)) || 'empty';
  // 注意：必須用 formData（即時 prop）而非 localFormData（async state）
  // 因為 key 改變（強制重新掛載）時，localFormData 可能還未更新
  const initialDefaultValue = (formData && (formData.defaultValue || formData.value)) || '';

  // 用戶按鍵時標記已實際輸入。
  // Ant Design TextArea 在掛載時（autoSize 計算）會觸發一次虛假 onChange，
  // 但此時用戶尚未按任何鍵。onKeyDown/onPaste 才代表真實輸入。
  const handleTextAreaKeyDown = () => {
    hasUserTyped.current = true;
  };

  const handleTextAreaPaste = () => {
    hasUserTyped.current = true;
  };

  // 處理 textarea 的變更
  const handleTextAreaChange = (e) => {
    // 若用戶尚未按鍵或貼上，忽略此次 onChange
    // （防止 Ant Design ResizableTextArea 掛載時的虛假事件）
    if (!hasUserTyped.current) {
      console.log('🔒 TextAreaEditor: 忽略用戶輸入前的 onChange 事件（防止虛假更新）');
      return;
    }
    const newValue = e.target.value;
    console.log('📝 textarea 預設值變更:', newValue);
    onFormChange('defaultValue', newValue);
    onFormChange('value', newValue);
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>{t('eformDesigner.editTextArea')}</h3>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('eformDesigner.placeholderText')}:</label>
        <Input
          value={localFormData.placeholder || ''}
          onChange={(e) => handleFormChange('placeholder', e.target.value)}
          placeholder={t('eformDesigner.pleaseEnterPlaceholderText')}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('eformDesigner.fieldName')}:</label>
        <Input
          value={localFormData.name || ''}
          onChange={(e) => handleFormChange('name', e.target.value)}
          placeholder={t('eformDesigner.pleaseEnterFieldName')}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('eformDesigner.defaultValue')}:</label>
        <TextArea
          key={componentKey}
          defaultValue={initialDefaultValue}
          onKeyDown={handleTextAreaKeyDown}
          onPaste={handleTextAreaPaste}
          onChange={handleTextAreaChange}
          placeholder={t('eformDesigner.pleaseEnterDefaultValue')}
          rows={4}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('eformDesigner.rows')}:</label>
        <Input
          type="number"
          value={localFormData.rows || 4}
          onChange={(e) => handleFormChange('rows', parseInt(e.target.value) || 4)}
          min={1}
          max={20}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('eformDesigner.columns')}:</label>
        <Input
          type="number"
          value={localFormData.cols || 50}
          onChange={(e) => handleFormChange('cols', parseInt(e.target.value) || 50)}
          min={1}
          max={100}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <input
            type="checkbox"
            checked={localFormData.required || false}
            onChange={(e) => handleFormChange('required', e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span style={{ fontWeight: 'bold' }}>{t('eformDesigner.requiredField')}</span>
        </label>
      </div>
    </div>
  );
};

export default TextAreaEditor;
